import {
  type BorderRadiusValue,
  type ContainerActionConfig,
  type ElementLabelPropsWithPositionAndWidth,
  type ElementSizingMode,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputFontStyleOptions,
  type PropertiesConfigInputSpacingAndAlignmentModel,
} from '@blaze-shared/elements-domain';
import {
  type SelectOption,
  type UnitOfMeasure,
  type ValueWithUnitOfMeasure,
} from '@blaze-shared/shared-util';

export type CountdownTemplate = 'block' | 'inline' | 'clock';
export type CountdownLabelsPosition = 'left' | 'top' | 'right' | 'bottom';

export const COUNTDOWN_TEMPLATE_OPTIONS: SelectOption<CountdownTemplate>[] = [
  { value: 'block', label: 'Block' },
  { value: 'inline', label: 'In-line' },
  { value: 'clock', label: 'Clock' },
];

export const COUNTDOWN_SHOW_OPTIONS: SelectOption<UnitOfMeasure>[] = [
  { value: 'days', label: 'Days' },
  { value: 'hrs', label: 'Hours' },
  { value: 'min', label: 'Mins' },
  { value: 's', label: 'Secs' },
];

export const COUNTDOWN_LABELS_POSITION_OPTIONS: SelectOption<CountdownLabelsPosition>[] = [
  { icon: 'icon_aligns_to_top_16', value: 'top', label: 'Top' },
  { icon: 'icon_aligns_to_right_16', value: 'right', label: 'Right' },
  { icon: 'icon_aligns_to_bottom_16', value: 'bottom', label: 'Bottom' },
  { icon: 'icon_aligns_to_left_16', value: 'left', label: 'Left' },
];

export const COUNTDOWN_DEFAULTS = {
  DEFAULT_WIDTH: 356,
  DEFAULT_HEIGHT: 40,
  DEFAULT_HEIGHT_LABEL_TOP: 72,
  MIN_SIZE: 40,
  MAX_WIDTH: 900,
  MAX_HEIGHT: 150,
  WIDTH_OPTIONS: ['fixed', 'full'] as ElementSizingMode[],
  HEIGHT_OPTIONS: ['min', 'fixed', 'auto'] as ElementSizingMode[],
};

export interface CountdownTimerPropsModel {
  content: {
    title: ElementLabelPropsWithPositionAndWidth;
    data: {
      targetField: string;
      action?: ContainerActionConfig;
    };
    options: {
      template: CountdownTemplate;
      duration: ValueWithUnitOfMeasure;
      show: UnitOfMeasure[];
      labelsPosition?: CountdownLabelsPosition;
      showDividers?: boolean;
    };
  };
  style: {
    general: {
      title: PropertiesConfigInputColorModel;
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
      fontFamily: string;
      fontSize: ValueWithUnitOfMeasure;
      fontStyle: PropertiesConfigInputFontStyleOptions;
      fontWeight: string;
      iconSize: ValueWithUnitOfMeasure;
      lineHeight: ValueWithUnitOfMeasure;
    };
    day: {
      fontFamily: string;
      fontWeight: string;
      fontSize: ValueWithUnitOfMeasure;
      fontStyle: PropertiesConfigInputFontStyleOptions;
      value: PropertiesConfigInputColorModel;
      fill: PropertiesConfigInputColorModel;
      border: PropertiesConfigInputColorModel;
      radius: BorderRadiusValue;
      borderWidth: ValueWithUnitOfMeasure;
    };
    time: {
      fill: PropertiesConfigInputColorModel;
      border: PropertiesConfigInputColorModel;
      borderWidth: ValueWithUnitOfMeasure;
      radius: BorderRadiusValue;
      divider: PropertiesConfigInputColorModel;
      dividerWidth: ValueWithUnitOfMeasure;
      textPart: 'value' | 'label';
      value: {
        fontFamily: string;
        fontWeight: string;
        fontSize: ValueWithUnitOfMeasure;
        fontStyle: PropertiesConfigInputFontStyleOptions;
        value: PropertiesConfigInputColorModel;
      };
      label: {
        fontFamily: string;
        fontWeight: string;
        fontSize: ValueWithUnitOfMeasure;
        fontStyle: PropertiesConfigInputFontStyleOptions;
        value: PropertiesConfigInputColorModel;
      };
    };
    label: {
      fontFamily: string;
      fontWeight: string;
      fontSize: ValueWithUnitOfMeasure;
      fontStyle: PropertiesConfigInputFontStyleOptions;
      value: PropertiesConfigInputColorModel;
      divider: PropertiesConfigInputColorModel;
    };
    clock: {
      gutter: PropertiesConfigInputColorModel;
      active: PropertiesConfigInputColorModel;
      width: ValueWithUnitOfMeasure;
    };
  };
}
